import React, { useEffect, useState, useCallback, useRef } from "react";
import MDEditor, { commands } from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { Button } from "@/components/ui/button";
import { PdfView } from "@/components/pdfView";
import { api } from "@/api/client";
import { getFileStream } from "@/api/client.ts";
import { toast } from "sonner";
import { debounce } from "lodash";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Download, Upload, X } from "lucide-react";
import { XMarkIcon } from "@heroicons/react/16/solid";
import "./index.css";
import pdfImage from "@/assets/search/pdf.png";
import mdImage from "@/assets/search/ic-MarkDown.svg";
import { useTranslation } from "@/locales";

// 本地测试时要渲染的 Markdown 内容
const markdownContent = ``;

interface SearchDocument {
  id?: string;
  title?: string;
  content?: string;
  url?: string;
  [key: string]: any; // 允许其他属性
}

interface viewFileEvent {
  type: string;
  record?: SearchDocument;
  index: number;
  showFileDetails?: boolean;
}

type UploadStatus = "pending" | "uploading" | "success" | "failed";

interface PendingMarkdownFile {
  id: string;
  file: File;
  name: string;
  page: number;
  chunkIndex?: number;
  status: UploadStatus;
  message?: string;
}

const pageChunkMarkdownNamePattern = /^([1-9]\d*)(?:-([1-9]\d*))?\.md$/;

function parsePageChunkMarkdownName(filename: string) {
  const match = filename.match(pageChunkMarkdownNamePattern);
  if (!match) return null;
  return {
    page: Number(match[1]),
    chunkIndex: match[2] ? Number(match[2]) : undefined,
  };
}

const getPendingMarkdownUploadOrder = (file: PendingMarkdownFile) => file.chunkIndex ?? 0;

function sortPendingMarkdownFiles(files: PendingMarkdownFile[]) {
  return [...files].sort((a, b) => a.page - b.page || getPendingMarkdownUploadOrder(a) - getPendingMarkdownUploadOrder(b) || a.name.localeCompare(b.name));
}

//转义
function escapeHtmlTags(content: string): string {
  return content.replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
// HTML 反转义函数（用于预览模式）
function unescapeHtmlTags(content: string): string {
  //console.log(content.replace(/&lt;/g, "<").replace(/&gt;/g, ">"))
  return content.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
}

export default function CheckEditor() {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.hash.split("?")[1]);
  const dataParam = urlParams.get("data");
  const data = dataParam ? JSON.parse(dataParam) : {};
  // 状态管理
  const [fileData, setFileData] = useState(data);
  const [currentPage, setCurrentPage] = useState<number>(1); //PDF组件的页面
  const [chunkData, setChunkData] = useState<any>(null); // 存储 chunk 接口返回的数据
  const [chunkDataOld, setChunkDataOld] = useState<any>(null); // 点击拆页后把旧数据保存，用于恢复
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>(""); // 存储 PDF Blob URL
  //左边预览区的总页数，也就是数组的长度
  const [numPages, setNumPages] = useState<number>(0);
  //右边PDF文件预览区的总页数
  const [pdfPages, setPdfPages] = useState<number>(0);
  const [isCheck, setIsCheck] = useState(false);
  //预览区的页码
  const [pageInput, setPageInput] = useState<number>(1);
  const [pageInputOld, setPageInputOld] = useState<number>(1);
  const [chunkList, setChunkList] = useState<any[]>([]); // 存储所有chunk数据
  const [DialogData, setDialogData] = useState({
    title: "",
    description: "",
    confirmText: "",
  });

  // 在组件挂载后设置翻译
  useEffect(() => {
    setDialogData({
      title: t("common.confirmDelete"),
      description: t("admin.deleteConfirmMessage"),
      confirmText: t("common.confirm"),
    });
  }, []);
  const confirmCheckDialogRef = useRef<any>(null);
  const deleteDialogRef = useRef<any>(null);
  const markdownValueDialogRef = useRef<any>(null);
  const [editOrView, setEditOrView] = useState<string>("view");

  // 处理编辑区内容变化（将转义后的内容反转义后存为原始内容）
  const debouncedSetValue = useCallback(
    debounce((value: string) => {
      setMarkdownValue(unescapeHtmlTags(value));
    }, 1000),
    []
  );
  // 处理编辑区内容变化（将转义后的内容反转义后存为原始内容）
  const handleEditorChange = (value: string) => {
    setMarkdownValue(value);
    debouncedSetValue(value);
  };

  const [messages, setMessages] = useState<any[]>([]);
  const [topicNo, setTopicNo] = useState<string>("");
  const [pdfType, setPdfType] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(true);
  // 在组件顶部添加状态管理动画
  const [sheetAnimation, setSheetAnimation] = useState<"entering" | "entered" | "exiting" | "exited">("exited");
  const [sheetVisible, setSheetVisible] = useState(false);
  const [viewFile, setViewFile] = useState<viewFileEvent>({
    type: "",
    index: 1,
    showFileDetails: false,
  });

  // Markdown 编辑器状态
  const [pdfValue, setPdfValue] = useState<string>("");
  const [markdownValue, setMarkdownValue] = useState<string>(markdownContent);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloadingChunks, setIsDownloadingChunks] = useState(false);
  const [isUploadingChunks, setIsUploadingChunks] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [pendingMarkdownFiles, setPendingMarkdownFiles] = useState<PendingMarkdownFile[]>([]);
  const markdownFileInputRef = useRef<HTMLInputElement>(null);

  const [openClose, setOpenClose] = useState(true);
  // 判断当前主题是否是深色模式
  const [isDarkMode, setIsDarkMode] = useState(false);

  // 监听主题切换
  useEffect(() => {
    // 检测当前主题
    console.log(localStorage.getItem("themeMode"));
    const checkTheme = () => {
      const darkMode = document.documentElement.classList.contains("dark") || window.matchMedia("(prefers-color-scheme: dark)").matches;
      setIsDarkMode(darkMode);
      if (localStorage.getItem("themeMode") === "light") {
        setIsDarkMode(false);
        //document.documentElement.setAttribute('data-color-mode', 'light')
      }
      if (localStorage.getItem("themeMode") === "dark") {
        setIsDarkMode(true);
        //document.documentElement.setAttribute('data-color-mode', 'light')
      }
    };

    // 初始检测
    checkTheme();

    // 监听主题变化
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // 监听系统主题变化
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const mediaQueryListener = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
      if (localStorage.getItem("themeMode") === "light") {
        setIsDarkMode(false);
      }
      if (localStorage.getItem("themeMode") === "dark") {
        setIsDarkMode(true);
      }
    };
    mediaQuery.addEventListener("change", mediaQueryListener);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", mediaQueryListener);
    };
  }, []);
  // 从url中获取参数data，并根据type获取内容
  // 修改 useEffect 中的 fetchContent 函数
  useEffect(() => {
    console.log("data:", fileData);

    const fetchContent = async () => {
      if (!fileData?.fileId) return;

      try {
        setIsLoading(true);

        if (fileData.type === "PDF") {
          // 获取 PDF Blob 数据
          getFileStream("/v1/knowledge/file/pdf/content", {
            fileId: fileData.fileId,
          }).then((blob) => {
            console.log(blob);
            const objectUrl = URL.createObjectURL(blob);
            setPdfBlobUrl(objectUrl);
            console.log(objectUrl);
          });

          // 获取第一页的 Markdown 内容
          await fetchChunkData(fileData.fileId, 1);
        } else if (fileData.type === "MD" || fileData.type === "TEXT") {
          // 获取 Markdown 内容
          const response = await api.get("/v1/admin/knowledge/file/markdown/content", {
            params: { fileId: fileData.fileId },
          });

          setEditOrView("edit");
          if (response?.content) {
            setChunkData(response);
            setMarkdownValue(response.content);
          }
        }
      } catch (error) {
        console.error(t("admin.getFileContentFailed"), error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();

    // 清理函数：组件卸载时释放创建的对象 URL
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        handlePageChange.cancel();
      }
    };
  }, [fileData]);

  // 点击页面左边编辑区的页码函数
  const clickPage = async (page: number) => {
    if (page < 1 || page > numPages || !fileData?.fileId) return;

    try {
      // 从 chunkList 中获取对应页面的数据
      if (chunkList && chunkList.length > 0) {
        const validPage = Math.min(Math.max(1, page), chunkList.length);
        const chunkItem = chunkList[validPage - 1]; // 数组索引从0开始
        // 更新当下预览或者 编辑的数据对象
        setChunkData(chunkItem);
        setChunkDataOld(chunkItem);
        setMarkdownValue(chunkItem.text || "");
        setPageInput(validPage);
      }
    } catch (error) {
      console.error("切换页面失败:", error);
      toast.error(t("admin.pageSwitchFailed"));
    } finally {
      setIsLoading(false);
    }
  };
  // 获取指定页码的 chunk 数据
  const fetchChunkData = async (fileId: string, page: number, flag: any, pageInputValue: any) => {
    try {
      const response = await api.get("/v1/admin/knowledge/file/chunk", {
        params: {
          fileId: fileId,
          page: page,
        },
      });

      // 处理数组响应格式
      if (response && Array.isArray(response) && response.length > 0) {
        setChunkList(response);

        // const chunkItem = response[validPage - 1]; // 数组索引从0开始
        let chunkItem = undefined;
        // chunkItem = response[0]
        // chunkItem = response[pageInputOld - 1]
        if (flag === "updateCurrentPage") {
          console.log('flag==="updateCurrentPage"');
          // 使用传递进来的 pageInputValue 或者默认值
          const pageIndex = pageInputValue - 1;
          chunkItem = response[pageIndex];
          setMarkdownValue(chunkItem.text || "");
          setChunkDataOld(chunkItem);
          setPageInput(pageInputValue);
          setNumPages(response.length);
        } else if (flag === "afterNewSave") {
          // 新增保存后，定位到最后一页
          const lastIndex = response.length - 1;
          chunkItem = response[lastIndex];
          setMarkdownValue(chunkItem.text || "");
          setChunkData(chunkItem);
          setChunkDataOld(chunkItem);
          setPageInput(response.length);
          setNumPages(response.length);
        } else {
          // const validPage = Math.min(Math.max(1, page), response.length);
          // debugger
          chunkItem = response[0];
          setMarkdownValue(chunkItem.text || "");
          setChunkData(chunkItem);
          setChunkDataOld(chunkItem);
          setCurrentPage(page);
          setPageInput(1);
          setNumPages(response.length);
        }
      } else {
        // 如果没有数据，清空编辑器内容
        setChunkData(null);
        setChunkDataOld(null);
        setMarkdownValue("");
        setCurrentPage(0);
        setPageInput(0);
        setNumPages(0);
        // toast.warning('未找到页面内容');
      }
    } catch (error) {
      console.error(t("admin.getPageContentFailed"), error);
      toast.error(t("admin.getPageContentFailed"));
    }
  };
  // 预览PDF组件的按钮点击时， 页面变化的函数
  const handlePageChange = useCallback(
    debounce(async (newPage: number) => {
      if (!fileData?.fileId) return;
      if (currentPage === pdfPages) {
        //最后一页了
        setIsCheck(true);
      }
      // 如果当前有未保存的编辑内容，可以在这里提示用户保存
      await fetchChunkData(fileData.fileId, newPage);
    }, 300), // 300ms 防抖延迟
    [fileData?.fileId, fetchChunkData]
  );

  const handleCheckSubmit = async () => {
    setDialogData({
      title: t("admin.confirmCheckComplete"),
      description: t("admin.confirmCheckCompleteMessage"),
      confirmText: t("admin.confirmCheck"),
    });
    confirmCheckDialogRef.current?.open();
  };
  //核对提交
  const checkSubmit = async () => {
    try {
      setIsSaving(true);

      const response = await api.post("/v1/admin/knowledge/file/checked", null, {
        params: {
          fileId: fileData.fileId,
        },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      toast.success(t("admin.checkSuccess"));
    } catch (error: any) {
      console.error(t("admin.checkFailed"), error);
      toast.error(error.data.msg || t("admin.checkFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  // 保存当前页内容
  const saveCurrentPage = async () => {
    // 检查必要数据
    if (!fileData?.fileId || !markdownValue) {
      toast.warning(t("admin.noContentToSave"));
      return;
    }

    try {
      setIsSaving(true);

      let response;

      if (fileData.type === "PDF") {
        // 根据 documentId 的存在与否决定调用哪个接口
        // debugger
        if (chunkData?.documentId) {
          // 调用更新接口
          response = await api.post("/v1/admin/knowledge/file/chunk/update", {
            documentId: chunkData.documentId,
            text: markdownValue,
          });
          if (response) {
            toast.success(t("status.updateSuccess"));
            await fetchChunkData(fileData.fileId, currentPage, "updateCurrentPage", pageInput);
          }
        } else {
          // 调用新增接口
          response = await api.post("/v1/admin/knowledge/file/chunk/new", {
            fileId: fileData.fileId,
            page: currentPage,
            text: markdownValue,
          });
          if (response) {
            toast.success(t("status.saveSuccess"));
            await fetchChunkData(fileData.fileId, currentPage, "afterNewSave", pageInput);
          }
        }
      }
      if (fileData.type === "MD") {
        // MD文件只有更新，没有新增，直接调用更新接口，页码默认为1
        response = await api.post("/v1/admin/knowledge/file/chunk/update", {
          title: chunkData.title,
          page: 1,
          text: markdownValue,
        });

        if (response) {
          toast.success(t("status.saveSuccess"));

          // 如果是新增操作，可能需要重新获取数据以获取新的 documentId
          if (!chunkData?.documentId) {
            // 重新获取当前页的数据，以获取新生成的 documentId
            await fetchChunkData(fileData.fileId, currentPage);
          }
        }
      }
    } catch (error) {
      console.error(t("status.saveFailed"), error);
      toast.error(t("status.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  // 保存 Markdown 内容到后端
  /*const handleSave = async () => {
        try {
            setIsSaving(true);
            // 测试用
            // const response = await api.post('/v1/admin/knowledge/file/content', {
            //     fileId: paramsId,
            //     content: markdownValue
            // });

            // 模拟保存成功
            setTimeout(() => {
                console.log(t("admin.contentSaveSuccess"), markdownValue);
                setIsSaving(false);
            }, 1000);
        } catch (error) {
            console.error(t("admin.saveContentError"), error);
            setIsSaving(false);
        }
    };*/
  //
  const handleSave = async () => {
    await saveCurrentPage();
  };

  const handleDownloadChunks = async () => {
    if (!fileData?.fileId || fileData.type !== "PDF") return;

    try {
      setIsDownloadingChunks(true);
      const blob = await getFileStream("/v1/admin/knowledge/file/chunks/export", {
        fileId: fileData.fileId,
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${fileData.name || "markdown"}-markdown.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      toast.success(t("file.downloadSuccess") || "下载成功");
    } catch (error) {
      console.error("download chunks failed", error);
      toast.error(t("file.downloadFailed") || "下载失败");
    } finally {
      setIsDownloadingChunks(false);
    }
  };

  const handleSelectMarkdownFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const existingNames = new Set(pendingMarkdownFiles.map((item) => item.name));
    const validFiles: PendingMarkdownFile[] = [];
    const invalidNames: string[] = [];
    const duplicateNames: string[] = [];

    selectedFiles.forEach((file) => {
      const parsedName = parsePageChunkMarkdownName(file.name);
      if (!parsedName) {
        invalidNames.push(file.name);
        return;
      }
      if (existingNames.has(file.name) || validFiles.some((item) => item.name === file.name)) {
        duplicateNames.push(file.name);
        return;
      }
      validFiles.push({
        id: `${file.name}-${file.lastModified}-${file.size}`,
        file,
        name: file.name,
        page: parsedName.page,
        chunkIndex: parsedName.chunkIndex,
        status: "pending",
      });
    });

    if (validFiles.length > 0) {
      setPendingMarkdownFiles((prev) => sortPendingMarkdownFiles([...prev, ...validFiles]));
      setIsUploadDialogOpen(true);
    }
    if (invalidNames.length > 0) {
      toast.warning(`已忽略命名不符合规则的文件：${invalidNames.join("、")}`);
    }
    if (duplicateNames.length > 0) {
      toast.warning(`已忽略重复文件：${duplicateNames.join("、")}`);
    }
    event.target.value = "";
  };

  const removePendingMarkdownFile = (id: string) => {
    setPendingMarkdownFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const clearFinishedMarkdownFiles = () => {
    setPendingMarkdownFiles((prev) => prev.filter((item) => item.status !== "success"));
  };

  const openMarkdownUpload = () => {
    if (pendingMarkdownFiles.length > 0) {
      setIsUploadDialogOpen(true);
      return;
    }
    markdownFileInputRef.current?.click();
  };

  const uploadPendingMarkdownFiles = async () => {
    const filesToUpload = sortPendingMarkdownFiles(pendingMarkdownFiles.filter((item) => item.status === "pending" || item.status === "failed"));
    if (!fileData?.fileId || filesToUpload.length === 0) {
      toast.warning(t("admin.noFilesToUpload") || "没有文件需要上传");
      return;
    }

    try {
      setIsUploadingChunks(true);
      const affectedPages = new Set<number>();
      let successCount = 0;

      for (const item of filesToUpload) {
        setPendingMarkdownFiles((prev) => prev.map((file) => (file.id === item.id ? { ...file, status: "uploading", message: "" } : file)));

        try {
          const formData = new FormData();
          formData.append("fileId", String(fileData.fileId));
          formData.append("file", item.file);
          await api.post("/v1/admin/knowledge/file/chunk/import", formData);
          affectedPages.add(item.page);
          successCount += 1;
          setPendingMarkdownFiles((prev) => prev.map((file) => (file.id === item.id ? { ...file, status: "success", message: "上传成功" } : file)));
        } catch (error: any) {
          const message = error?.data?.msg || error?.message || "上传失败";
          setPendingMarkdownFiles((prev) => prev.map((file) => (file.id === item.id ? { ...file, status: "failed", message } : file)));
          toast.error(`${item.name} ${message}`);
        }
      }

      if (affectedPages.has(currentPage)) {
        await fetchChunkData(fileData.fileId, currentPage);
      }
      if (successCount > 0) {
        toast.success(`上传完成，成功 ${successCount} 个文件`);
      }
    } finally {
      setIsUploadingChunks(false);
    }
  };

  // 添加处理确认对话框操作的函数
  const handleConfirmSave = async () => {
    await saveCurrentPage();
    setEditOrView("view");
    setOpenClose(true);
  };

  const handleConfirmDiscard = () => {
    // 恢复原始内容
    setMarkdownValue(chunkDataOld?.text || "");
    setChunkData(chunkDataOld);
    setEditOrView("view");
    setOpenClose(true);
  };
  // 点击编辑切换模式
  const handleEdit = () => {
    console.log("chunkDataOld", chunkDataOld?.text);
    if (editOrView === "edit") {
      if (markdownValue !== chunkDataOld?.text) {
        //如果有修改，弹出二次确认弹窗问用户是否保存，点击保存触发saveCurrentPage函数保存，点击取消则取回chunkDataOld.text的值给markdownValue，再切回预览模式
        markdownValueDialogRef.current?.open();
        return;
      }
    }
    if (openClose && !chunkData) {
      //在拆页时点击了预览，再点击编辑，如果没有!chunkData说明是拆页状态，则隐藏关闭/编辑按钮
      setOpenClose(false);
    }
    setEditOrView((prev) => (prev === "edit" ? "view" : "edit"));
    // toast.info('编辑功能正在开发中...');
  };

  // 点击拆分页
  const handleSplitPage = () => {
    //拆页后要清空内容，让保存时走新增的接口
    setChunkData(null);
    setMarkdownValue("");
    setOpenClose(false);
    // toast.info('拆分一页功能正在开发中...');
  };
  const handleClose = () => {
    //点击返回要把旧数据恢复
    setMarkdownValue(chunkDataOld.text);
    setChunkData(chunkDataOld);
    setOpenClose(true);
  };

  //确认删除
  const handleDeletePage = async () => {
    // 确保有 chunkData 和 documentId
    if (!chunkData?.documentId) {
      toast.warning(t("common.noContentToDelete"));
      return;
    }
    try {
      setIsSaving(true);

      const response = await api.post("/v1/admin/knowledge/file/chunk/delete", null, {
        params: {
          documentId: chunkData.documentId,
        },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (response) {
        toast.success(t("common.deleteSuccess") || "删除成功");
        setChunkData(null);
        setChunkDataOld(null);
        setMarkdownValue("");
        await fetchChunkData(fileData.fileId, currentPage);
        // 重新加载 PDF 内容
        /*getFileStream('/v1/knowledge/file/pdf/content', {
                    fileId: fileData.fileId,
                }).then((blob) => {
                    // 清理旧的 URL
                    if (pdfBlobUrl) {
                        URL.revokeObjectURL(pdfBlobUrl);
                    }

                    // 创建新的 URL
                    const objectUrl = URL.createObjectURL(blob);
                    setPdfBlobUrl(objectUrl);
                });*/
        // 如果还有其他页面，加载下一页或上一页
        /*if (numPages > 1) {
                    if (currentPage > 1) {
                        // 加载上一页
                        await fetchChunkData(fileData.fileId, currentPage - 1);
                    } else {
                        // 加载下一页
                        await fetchChunkData(fileData.fileId, currentPage + 1);
                    }
                } else {
                    // 如果这是最后一页，清空内容
                    setChunkData(null);
                    setMarkdownValue('');
                }*/
      }
    } catch (error) {
      console.error(t("common.deleteFailed"), error);
      toast.error(t("common.deleteFailed") || "删除失败");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <div className="bg-background flex" style={{ borderRadius: 16, display: "flex" }}>
        <div className="ai-chat-container bg-background" style={{ width: "50%", height: "99vh", padding: "0 10px" }}>
          <div className="EditorLeft-top-bar top-0 bg-[var(--background)] z-10 p-2 border-b w-full justify-center ">
            {/*<div className="pdf-file-name" style={{display:'flex'}}>
                            {fileData.type === 'PDF' && (
                                <img src={pdfImage} style={{width:'20px',marginRight:'10px'}} alt="PDF"/>
                            )}
                            {fileData.type === 'MD' && (
                                <img src={mdImage} style={{width:'20px',marginRight:'10px'}} alt="MD"/>
                            )}
                            {fileData ? fileData.name : ''}
                        </div>*/}

            <div style={{ opacity: 0 }}>本地的测试文件副本.pdf</div>

            {fileData?.type === "PDF" && numPages > 0 && (
              <div className="flex items-center">
                <Pagination className="mx-0">
                  <PaginationContent>
                    <PaginationItem>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => clickPage(pageInput - 1)} disabled={pageInput <= 1}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    </PaginationItem>

                    <PaginationItem>
                      <div className="flex items-center gap-1 px-2">
                        <form className="flex items-center gap-1">
                          <input type="text" value={pageInput} disabled className="w-12 border rounded px-1 text-center" />
                          <span className="text-sm text-gray-600">/ {numPages}</span>
                        </form>
                      </div>
                    </PaginationItem>

                    <PaginationItem>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => clickPage(pageInput + 1)} disabled={pageInput >= (numPages ?? 0)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}

            {/* 按钮 */}
            <div style={{ height: "32px", minWidth: "232px", textAlign: "end" }}>
              {fileData?.type === "PDF" && (
                <>
                  <Button style={{ height: "30px", width: "72px", marginLeft: "1rem", verticalAlign: "middle" }} size="sm" variant="outline" onClick={handleDownloadChunks} disabled={isDownloadingChunks || isUploadingChunks}>
                    <Download className="h-3.5 w-3.5" />
                    下载
                  </Button>
                  <Button style={{ height: "30px", width: "72px", marginLeft: "1rem", verticalAlign: "middle" }} size="sm" variant="outline" onClick={openMarkdownUpload} disabled={isUploadingChunks}>
                    <Upload className="h-3.5 w-3.5" />
                    上传
                  </Button>
                  <input ref={markdownFileInputRef} type="file" accept=".md" multiple className="hidden" onChange={handleSelectMarkdownFiles} />
                </>
              )}
              {/*只有当下数据存在数据库才能删除,并且不是拆页，而且是最后一页时预览状态时才能核对*/}
              {isCheck && chunkData?.documentId && editOrView === "view" && openClose && (
                <Button style={{ height: "30px", marginLeft: "1rem" }} size="sm" onClick={handleCheckSubmit} disabled={isSaving}>
                  {isSaving ? t("admin.operating") : t("admin.completeCheck")}
                </Button>
              )}
              {/*不是MD类型 而且是编辑状态才能拆页*/}
              {fileData.type === "PDF" && editOrView === "edit" && (
                <>
                  {openClose && chunkData ? (
                    <Button style={{ height: "30px", marginLeft: "1rem" }} size="sm" onClick={handleSplitPage} disabled={isSaving}>
                      {isSaving ? t("admin.operating") : t("admin.splitPage")}
                    </Button>
                  ) : (
                    <Button style={{ height: "30px", marginLeft: "1rem" }} size="sm" variant="outline" onClick={handleClose}>
                      {t("common.back")}
                    </Button>
                  )}
                </>
              )}
              {openClose && (
                <Button style={{ height: "30px", marginLeft: "1rem" }} size="sm" onClick={handleEdit} disabled={isSaving}>
                  {isSaving ? t("admin.operating") : editOrView === "edit" ? t("common.close") : t("common.edit")}
                </Button>
              )}

              {/*只有编辑状态才能保存*/}
              {editOrView === "edit" && (
                <Button style={{ height: "30px", marginLeft: "1rem" }} size="sm" onClick={saveCurrentPage} disabled={isSaving}>
                  {isSaving ? t("admin.operating") : t("common.save")}
                </Button>
              )}
              {/*只有当下数据存在数据库才能删除,并且不是拆页，预览状态时才能删除*/}
              {chunkData?.documentId && editOrView === "view" && openClose && (
                <Button
                  style={{ height: "30px", marginLeft: "1rem" }}
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    setDialogData({
                      title: t("admin.tip"),
                      description: t("admin.confirmDeleteCurrentPage"),
                      confirmText: t("common.confirmDelete"),
                    });
                    deleteDialogRef.current?.open();
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? t("admin.operating") : t("common.delete")}
                </Button>
              )}
            </div>
          </div>
          <div style={{ width: "100%", height: "837px", overflow: "auto" }} data-color-mode={isDarkMode ? "dark" : "light"}>
            {editOrView === "edit" && (
              //setEditOrView('view');
              //value={escapeHtmlTags(markdownValue)}
              //onChange={handleEditorChange}
              <MDEditor
                value={escapeHtmlTags(markdownValue)}
                onChange={handleEditorChange}
                height={"100%"}
                preview="edit"
                components={{
                  toolbar: (command, disabled, executeCommand) => {
                    // 判断是否为 fullscreen 按钮
                    const isFullscreenButton = command.name === "fullscreen" || command.name === "live";
                    return (
                      <button
                        key={command.name}
                        disabled={disabled}
                        onClick={() => {
                          // 点击前的回调逻辑
                          console.log(`即将执行命令: ${command.name}`);
                          if (command.name === "preview") {
                            setEditOrView("view");
                            setOpenClose(true);
                          } else {
                            executeCommand(command, command.name);
                          }
                          // 执行命令
                          // executeCommand(command, command.name);
                        }}
                        style={{
                          fontSize: "16px", // 增大字体
                          margin: "0 2px",
                          display: isFullscreenButton ? "none" : undefined,
                        }}
                        title={command.buttonProps?.title}
                      >
                        {command.icon}
                      </button>
                    );
                  },
                  // 自定义预览区域样式
                }}
                commands={[
                  commands.bold,
                  commands.italic,
                  commands.strikethrough,
                  commands.hr,
                  commands.title,
                  commands.link,
                  commands.quote,
                  commands.code,
                  commands.codeBlock,
                  commands.unorderedListCommand,
                  commands.orderedListCommand,
                  commands.checkedListCommand,
                  // 添加表格命令
                ]}
              />
            )}

            {editOrView === "view" && (
              <div className="content answer-content p-4">
                {markdownValue ? <MDEditor.Markdown source={markdownValue} /> : <div className="text-center text-gray-500 py-8">{t("admin.pageContentNotFound")}</div>}
              </div>
            )}
          </div>
        </div>

        <div className="messageAadmin answer" style={{ width: "50%", height: "99vh", overflow: "auto" }}>
          {fileData?.type === "PDF" ? (
            <div className="h-full flex-1 overflow-y-auto">
              <PdfView
                file={pdfBlobUrl}
                initialPage={currentPage}
                onPageChange={handlePageChange}
                record={fileData}
                onDocumentLoadSuccess={({ numPages }) => {
                  setPdfPages(numPages);
                }}
              ></PdfView>
            </div>
          ) : fileData?.type === "MD" || fileData?.type === "TEXT" ? (
            <>
              <div className="EditorLeft-top-bar top-0 bg-[var(--background)] z-10 p-2 border-b w-full justify-center ">
                {/*顶部*/}
                <div>{fileData.name}</div>
              </div>
              <div className="content answer-content p-4">
                <MDEditor.Markdown source={markdownValue} />
              </div>
            </>
          ) : (
            <div style={{ padding: "20px" }}>
              {t("admin.unsupportedFileType")}: {fileData?.type}
            </div>
          )}
        </div>
      </div>

      {/*<div style={{paddingTop:'0.3rem',paddingBottom:'0.5rem',paddingLeft: '16px'}}>*/}
      {/*    /!*<Button*/}
      {/*        size="sm"*/}
      {/*        variant="outline"*/}
      {/*        onClick={handleClose}*/}
      {/*    >*/}
      {/*        返 回*/}
      {/*    </Button>*!/*/}
      {/*    <Button*/}
      {/*        style={{height: '30px', marginRight:'1rem'}}*/}
      {/*        size="sm"*/}
      {/*        onClick={handleEdit}*/}
      {/*        disabled={isSaving}*/}
      {/*    >*/}
      {/*        {isSaving ? '操作中...' : (editOrView === 'edit' ? '预览' : '编辑')}*/}
      {/*    </Button>*/}
      {/*    /!*不是MD类型 而且是编辑状态才能拆页*!/*/}
      {/*    {fileData.type === "PDF"&& editOrView === "edit"  && (*/}
      {/*        <>*/}
      {/*            {openClose ?*/}
      {/*            (*/}
      {/*            <Button*/}
      {/*            style={{height: '30px', marginRight:'1rem'}}*/}
      {/*            size="sm"*/}
      {/*            onClick={handleSplitPage}*/}
      {/*            disabled={isSaving}*/}
      {/*        >*/}
      {/*            {isSaving ? '操作中...' : '拆页'}*/}
      {/*            </Button> ):*/}
      {/*            (*/}
      {/*            <Button*/}
      {/*                style={{height: '30px', marginRight:'1rem'}}*/}
      {/*                size="sm"*/}
      {/*                variant="outline"*/}
      {/*                onClick={handleClose}*/}
      {/*            >*/}
      {/*                返回*/}
      {/*            </Button>*/}
      {/*            )}*/}

      {/*        </>*/}

      {/*    )}*/}
      {/*    /!*只有编辑状态才能保存*!/*/}
      {/*    {editOrView === "edit" && (*/}
      {/*        <Button*/}
      {/*            style={{height: '30px', marginRight:'1rem'}}*/}
      {/*            size="sm"*/}
      {/*            onClick={saveCurrentPage}*/}
      {/*            disabled={isSaving}*/}
      {/*        >*/}
      {/*            {isSaving ? '操作中...' : '保存'}*/}
      {/*        </Button>*/}
      {/*    )}*/}
      {/*    /!*只有当下数据存在数据库才能删除,并且不是拆页，预览状态时才能删除*!/*/}
      {/*    {chunkData?.documentId&& editOrView === "view" && openClose && (*/}
      {/*        <Button*/}
      {/*            style={{height: '30px', marginRight:'1rem'}}*/}
      {/*            size="sm"*/}
      {/*            variant="destructive"*/}
      {/*            onClick={()=>{*/}
      {/*                setDialogData({*/}
      {/*                    title: "提示",*/}
      {/*                    description: `确定要删除当前页吗？此操作无法撤销。`,*/}
      {/*                    confirmText: "确定删除"*/}
      {/*                });*/}
      {/*                deleteDialogRef.current?.open();*/}
      {/*            }}*/}
      {/*            disabled={isSaving}*/}
      {/*        >*/}
      {/*            {isSaving ? '操作中...' : '删除'}*/}
      {/*        </Button>*/}
      {/*    )}*/}
      {/*</div>*/}

      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="sm:max-w-[760px]" style={{ padding: 0 }}>
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>上传 Markdown 文件</DialogTitle>
          </DialogHeader>

          <div className="px-6 py-4">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                共 {pendingMarkdownFiles.length} 个文件，待上传 {pendingMarkdownFiles.filter((item) => item.status === "pending").length} 个，成功 {pendingMarkdownFiles.filter((item) => item.status === "success").length} 个，失败 {pendingMarkdownFiles.filter((item) => item.status === "failed").length} 个
              </div>
              <Button size="sm" variant="outline" onClick={() => markdownFileInputRef.current?.click()} disabled={isUploadingChunks}>
                <Upload className="h-4 w-4" />
                继续选择
              </Button>
            </div>

            <div className="overflow-hidden rounded-md border">
              <div className="grid grid-cols-[minmax(0,1fr)_72px_80px_92px_44px] items-center border-b bg-muted/40 px-3 py-2 text-sm font-medium">
                <div>文件名</div>
                <div className="text-center">页码</div>
                <div className="text-center">Chunk</div>
                <div className="text-center">状态</div>
                <div />
              </div>
              <div className="max-h-[360px] overflow-auto">
                {pendingMarkdownFiles.length === 0 ? (
                  <div className="px-3 py-10 text-center text-sm text-gray-500">暂无待上传文件</div>
                ) : (
                  pendingMarkdownFiles.map((item) => (
                    <div key={item.id} className="grid grid-cols-[minmax(0,1fr)_72px_80px_92px_44px] items-center border-b px-3 py-2 text-sm last:border-b-0">
                      <div className="min-w-0">
                        <div className="truncate font-mono" title={item.name}>
                          {item.name}
                        </div>
                        {item.message && <div className="mt-1 truncate text-xs text-gray-500" title={item.message}>{item.message}</div>}
                      </div>
                      <div className="text-center">{item.page}</div>
                      <div className="text-center">{item.chunkIndex || "-"}</div>
                      <div className={item.status === "failed" ? "text-center text-red-500" : item.status === "success" ? "text-center text-green-600" : "text-center text-gray-500"}>
                        {item.status === "pending" && "待上传"}
                        {item.status === "uploading" && "上传中"}
                        {item.status === "success" && "成功"}
                        {item.status === "failed" && "失败"}
                      </div>
                      <div className="text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removePendingMarkdownFile(item.id)} disabled={isUploadingChunks || item.status === "uploading"}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button variant="outline" onClick={clearFinishedMarkdownFiles} disabled={isUploadingChunks || !pendingMarkdownFiles.some((item) => item.status === "success")}>
              清除已成功
            </Button>
            <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)} disabled={isUploadingChunks}>
              关闭
            </Button>
            <Button onClick={uploadPendingMarkdownFiles} disabled={isUploadingChunks || !pendingMarkdownFiles.some((item) => item.status === "pending" || item.status === "failed")}>
              {isUploadingChunks ? "上传中" : "开始上传"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        ref={markdownValueDialogRef}
        title={t("admin.contentNotSaved")}
        description={t("admin.contentNotSavedDescription")}
        confirmText={t("common.save")}
        cancelText={t("admin.discardChanges")}
        onConfirm={handleConfirmSave}
        onCancel={handleConfirmDiscard}
      />

      <ConfirmDialog
        ref={confirmCheckDialogRef}
        title={DialogData.title}
        description={DialogData.description}
        confirmText={DialogData.confirmText}
        cancelText={t("common.cancel")}
        onConfirm={() => {
          checkSubmit();
        }}
      />

      {/* 确认弹窗 */}
      <ConfirmDialog
        ref={deleteDialogRef}
        title={DialogData.title}
        description={DialogData.description}
        confirmText={DialogData.confirmText}
        cancelText={t("common.cancel")}
        onConfirm={() => {
          handleDeletePage();
        }}
      />
    </div>
  );
}
